import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fetchGroupDetails } from "../../features/itemSlice";

const getDisplayNames = (value, fallback = "-") => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : fallback;
  }

  if (typeof value === "string") {
    return value.trim() || fallback;
  }

  return fallback;
};

const ItemDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  useEffect(() => {
    if (!id) {
      navigate("/items");
      return;
    }
    dispatch(fetchGroupDetails({ groupId: id }));
  }, [dispatch, id, navigate]);

  const { groupData, isFetch } = useSelector((state) => state.item);
  const items = Array.isArray(groupData?.items) ? groupData.items.filter(Boolean) : [];
  const statusLabel = groupData?.status === "2" ? "Published" : "Unpublished";

  return (
    <div id="main" className="main">
      <div className="pagetitle">
        <h1>Challenge Pack Details</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/items">Challenge Packs</Link>
            </li>
            <li className="breadcrumb-item active">Details</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="container">
          {isFetch ? (
            <div className="text-center py-3">Loading...</div>
          ) : !groupData ? (
            <div className="text-center py-3 text-muted">No package details found.</div>
          ) : (
            <div className="d-grid gap-3">
              <div className="d-flex align-items-start gap-3 flex-wrap">
                {groupData.banner_img_url ? (
                  <img
                    src={groupData.banner_img_url}
                    alt={`${groupData.group_name || "Package"} Banner`}
                    style={{ width: 160, aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 8 }}
                  />
                ) : null}

                <div className="flex-grow-1">
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <tbody>
                        {groupData.group_image_url ? (
                          <tr>
                            <th style={{ width: 180 }}>Logo</th>
                            <td>
                              <img
                                src={groupData.group_image_url}
                                alt={`${groupData.group_name || "Package"} Logo`}
                                style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8 }}
                              />
                            </td>
                          </tr>
                        ) : null}
                        <tr>
                          <th style={{ width: 180 }}>Challenge Pack Name</th>
                          <td>{groupData.group_name || "-"}</td>
                        </tr>
                        <tr>
                          <th>Description</th>
                          <td style={{ whiteSpace: "pre-wrap" }}>{groupData.group_description || "-"}</td>
                        </tr>
                        <tr>
                          <th>Category</th>
                          <td>{getDisplayNames(groupData.categoryNames, groupData.category_name || "-")}</td>
                        </tr>
                        <tr>
                          <th>Sub-category</th>
                          <td>{getDisplayNames(groupData.subCategoryNames, groupData.sub_category_name || "-")}</td>
                        </tr>
                        <tr>
                          <th>Price</th>
                          <td>{groupData.price ?? "-"}</td>
                        </tr>
                        <tr>
                          <th>Status</th>
                          <td>
                            <span className={`badge ${groupData.status === "2" ? "bg-success" : "bg-secondary"}`}>
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>Apple Product ID</th>
                          <td>{groupData.apple_product_id || "-"}</td>
                        </tr>
                        <tr>
                          <th>Android Product ID</th>
                          <td>{groupData.android_product_id || "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div>
                <h6 className="mb-3">Challenges ({items.length})</h6>
                {items.length ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th className="text-center">Image</th>
                          <th>Challenge Name</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={`item-group-item-${item.id || index}`}>
                            <td className="text-center" style={{ width: 90 }}>
                              {item.item_iamge_url ? (
                                <img
                                  src={item.item_iamge_url}
                                  alt={item.item_name}
                                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8 }}
                                />
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{item.item_name || "-"}</td>
                            <td style={{ whiteSpace: "pre-wrap" }}>{item.item_description || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-muted">No challenges found.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ItemDetails;
